import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, deleteData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import LogoLoader from '../../common/LogoLoader';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import Pagination from '../../common/Pagination';
import ProductionModal from './ProductionModal';
import {
  Button, IconButton, Tooltip, Box, Typography, TextField, InputAdornment,
  Table, TableBody, TableCell, TableRow,
  Dialog, DialogContent, DialogActions, useMediaQuery
} from '@mui/material';
import {
  Search, Add, Refresh, Visibility, Edit, Delete
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import './ProductionIndex.css';

const ProductionIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  // Format voucher number as PDT/VoucherNo/FinYear
  const formatVoucherNo = (voucherNo, voucherDate) => {
    if (!voucherNo) return '-';
    let finYear = '';
    if (voucherDate) {
      const d = new Date(voucherDate);
      const month = d.getMonth(); // 0-based
      const year = d.getFullYear();
      // Financial year: Apr to Mar
      const startYear = month >= 3 ? year : year - 1;
      const endYear = startYear + 1;
      finYear = `${String(startYear).slice(2)}-${String(endYear).slice(2)}`;
    }
    return `PDT/${voucherNo}${finYear ? '/' + finYear : ''}`;
  };

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Data states
  const [data, setData] = useState([]);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  // Date filters
  const formatDateToYYYYMMDD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  const fetchProductions = async () => {
    try {
      const response = await fetchData('ProductionApi', `/ProductionList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const list = response.response_data || response || [];
      setData(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching production data:', error);
      setData([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ProductionApi', `/Items/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchAllItems = async () => {
    try {
      const response = await fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/0`, token);
      const items = response.response_data || response || [];
      const rawMaterials = items.filter(item => item.IsRawMaterial === true);
      setAllItems(rawMaterials);
    } catch (error) {
      console.error('Error fetching all items:', error);
      setAllItems([]);
    }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Loading productions...');
    try {
      await Promise.allSettled([
        fetchProductions(),
        fetchItems(),
        fetchAllItems()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  const handleRefresh = () => {
    fetchAllData();
  };

  // Modal handlers
  const handleOpenModal = async (mode, record = null) => {
    setModalMode(mode);
    if (mode === 'view') {
      const recordId = record?.Id || 0;
      logAudit(AUDIT_ACTION.VIEW, 'ProductionApi', recordId);
    }
    if (record && record.Id && mode !== 'create') {
      setModalLoading(true);
      try {
        const response = await fetchData('ProductionApi', `/ProductionData/${record.Id}`, token);
        const detail = response?.response_data || response || record;
        if (detail?.Production) {
          setSelectedRecord({ ...detail.Production, ProductionDetails: detail.ProductionDetails || [] });
        } else {
          setSelectedRecord(detail);
        }
      } catch {
        setSelectedRecord(record);
      }
      setModalLoading(false);
    } else {
      setSelectedRecord(null);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRecord(null);
    setModalMode('create');
  };

  const handleSave = () => {
    fetchProductions();
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, voucherNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(voucherNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('ProductionApi', deleteItemId, token);
      logAudit(AUDIT_ACTION.DELETE, 'ProductionApi', deleteItemId, { VoucherNumber: deleteItemNo }, null);
      await fetchProductions();
    } catch (error) {
      console.error('Error deleting production:', error);
    }
    setLoading(false);
  };

  // Filter data
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.ItemName || '').toLowerCase().includes(term) ||
      (item.VoucherNumber || '').toString().includes(term)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || modalLoading} message={loadingMessage} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Production Management</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="New Production" arrow>
                <IconButton onClick={() => handleOpenModal('create')} disabled={!hasCreateMasterPermission} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                  <Add sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Voucher No, Product..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Voucher No, Product..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => handleOpenModal('create')}
              disabled={!hasCreateMasterPermission}
              sx={{
                background: '#2C3E6E',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                minHeight: '29px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { background: '#1a2d5a' }
              }}
            >
              New Production
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableBody>
              {/* Header Row */}
              <TableRow sx={{ background: 'linear-gradient(135deg, #1d2445 0%, #1b0b2b 100%)' }}>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap' }}>#</TableCell>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap' }}>Voucher No</TableCell>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap' }}>Product</TableCell>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap' }}>Narration</TableCell>
                <TableCell sx={{ color: 'white', fontSize: '11px', fontWeight: 600, padding: '8px 10px', whiteSpace: 'nowrap', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: '#999', padding: '32px', fontSize: '13px' }}>
                    No production records found
                  </TableCell>
                </TableRow>
              ) : paginatedData.map((record, index) => (
                <TableRow
                  key={record.Id}
                  sx={{
                    '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                    '&:hover': { backgroundColor: '#e8f0fe' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell sx={{ fontSize: '11px', padding: '6px 10px', color: '#555' }}>
                    {(currentPage - 1) * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', padding: '6px 10px', fontWeight: 600, color: '#1d2445' }}>
                    {formatVoucherNo(record.VoucherNumber, record.VoucherDate)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', padding: '6px 10px' }}>
                    {record.VoucherDate ? new Date(record.VoucherDate).toLocaleDateString('en-GB') : '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', padding: '6px 10px', fontWeight: 500 }}>
                    {record.ItemName || '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', padding: '6px 10px' }}>
                    {record.Narration || '-'}
                  </TableCell>
                  <TableCell sx={{ padding: '6px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <Tooltip title="View" arrow>
                      <IconButton size="small" onClick={() => handleOpenModal('view', record)} sx={{ color: '#1d2445', padding: '3px' }}>
                        <Visibility sx={{ fontSize: '15px' }} />
                      </IconButton>
                    </Tooltip>
                    {hasEditMasterPermission && (
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => handleOpenModal('edit', record)} sx={{ color: '#45a359', padding: '3px' }}>
                          <Edit sx={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasDeleteMasterPermission && (
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => handleOpenDeleteModal(record.Id, record.VoucherNumber)} sx={{ color: '#e53935', padding: '3px' }}>
                          <Delete sx={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ mt: 1 }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={rowsPerPage}
              onItemsPerPageChange={(val) => { setRowsPerPage(val); setCurrentPage(1); }}
              totalItems={filteredData.length}
            />
          </Box>
        )}
      </div>

      {/* Production Modal */}
      <ProductionModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        mode={modalMode}
        recordData={selectedRecord}
        items={items}
        allItems={allItems}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Delete Production?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to delete production <strong>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={handleCloseDeleteModal}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProductionIndex;
